/**
 * KAN-468 — "Complete your profile" prompt.
 *
 * Non-blocking banner shown to a learner whose active org has requested
 * profile fields (isRequested) that are still unfilled — e.g. right after
 * invite acceptance / first login. Links to the profile page's My
 * Information section.
 *
 * Dismissal persistence (documented choice, KAN-468):
 * - X (close): sessionStorage marker -> hidden for this browser session,
 *   reappears on the next login/session until the fields are complete.
 * - "Don't show again": localStorage marker 'permanent' -> never shown again
 *   on this browser for this user+org.
 * Both markers are scoped per user AND per org (promptDismissalKey), so a
 * dismissal in one org cannot hide another org's prompt. Storage access is
 * wrapped so a storage-less environment degrades to "always show".
 *
 * Only learners are prompted: the requested fields are learner profile data,
 * and instructors/admins manage them from the compliance admin UI instead.
 */
import { ChangeDetectorRef, Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ProfileService } from '../profile.service';
import { ProfileFieldsService } from './profile-fields.service';
import { promptDismissalKey, shouldShowProfilePrompt } from './profile-fields.utils';

@Component({
  selector: 'app-complete-profile-prompt',
  standalone: true,
  imports: [CommonModule, RouterModule, IconComponent],
  templateUrl: './complete-profile-prompt.component.html',
  styleUrls: ['./complete-profile-prompt.component.scss'],
})
export class CompleteProfilePromptComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private profileService = inject(ProfileService);
  private profileFieldsService = inject(ProfileFieldsService);

  visible = false;
  private storageKey: string | null = null;

  ngOnInit(): void {
    const user = this.profileService.getCurrentUser();
    if (!user?.id || !user.currentOrgId || user.currentOrgRole !== 'learner') {
      return;
    }
    this.storageKey = promptDismissalKey(user.id, user.currentOrgId);
    if (this.readLocal() === 'permanent' || this.readSession() !== null) {
      return;
    }
    this.profileFieldsService
      .getMyProfileFields()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ completion }) => {
          this.visible = shouldShowProfilePrompt(completion, this.readLocal(), false);
          this.cdr.markForCheck();
        },
        // Non-blocking by design: on any error the prompt simply stays hidden.
        error: () => {
          this.visible = false;
          this.cdr.markForCheck();
        },
      });
  }

  /** X — hide for the rest of this session. */
  dismissForSession(): void {
    try {
      if (this.storageKey) {
        sessionStorage.setItem(this.storageKey, 'session');
      }
    } catch {
      // Storage unavailable: dismissal is in-memory only.
    }
    this.visible = false;
  }

  /** "Don't show again" — never show for this user+org on this browser. */
  dismissPermanently(): void {
    try {
      if (this.storageKey) {
        localStorage.setItem(this.storageKey, 'permanent');
      }
    } catch {
      // Storage unavailable: dismissal is in-memory only.
    }
    this.visible = false;
  }

  private readLocal(): string | null {
    try {
      return this.storageKey ? localStorage.getItem(this.storageKey) : null;
    } catch {
      return null;
    }
  }

  private readSession(): string | null {
    try {
      return this.storageKey ? sessionStorage.getItem(this.storageKey) : null;
    } catch {
      return null;
    }
  }
}
